import React, {
  useCallback,
  useState,
  useMemo,
} from 'react';
import {
  Form,
  Select,
  Divider,
  Row,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { getProperty } from '../helpers/helpers';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import SimpleTextInputModal from '../common/modals/SimpleTextInputModal';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import BoardDropdownFieldSelector from '../boards/BoardDropdownFieldSelector';
import { getProjectBucketTemplates } from '../buckets/bucketHelpers';
import {
  addUserProjectFilterView,
  deleteUserProjectFilterView,
  updateProjectFilters,
  updateUserProjectFilterView,
  fetchFilteredProjectIds,
} from './state/projects.actions';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import { PROJECT_FILTER_VIEW_FIELDS } from './projects.constants';
import { defaultFilters } from './projectFilters';

import useToggle from '../common/hooks/useToggle';

const VALID_DROPDOWN_DATA_TYPES = new Set([
  'Customers',
  'Projects',
  'Users',
  'Equipment',
  'Vendors',
  'Buckets',
  'Contacts',
  'Custom',
  'Forms',
  'Labels',
  'ScheduleCategories',
  'Divisions',
]);

export default function ProjectsCustomFilterTab({
  projectTypes = [],
  labels = [],
  onClose,
  filterView = {},
  setActiveTab,
  currentProjectTypeId = null,
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const {
    projectFilters: activeFilters = {},
    customFieldTemplate = {},
  } = useSelector((state) => state.projects);
  const divisions = useSelector((state) => state.settings.divisions);
  const users = useSelector((state) => state.users.users);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const buckets = useSelector((state) => state.buckets.buckets);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastFilterId, setLastFilterId] = useState(null);

  const {
    isToggled: isSaving,
    toggle: toggleSaving,
  } = useToggle();

  const currentFilters = useMemo(() => {
    if (!filterView || activeFilters.filterView === filterView.id) {
      return activeFilters;
    }
    return filterView;
  }, [filterView, activeFilters]);

  const formTitle = useMemo(() => {
    if (filterView?.id) {
      return `Edit ${filterView.filterViewName} View`;
    }

    return 'Custom Filter';
  }, [filterView]);

  // ----------------
  // Dropdown Options
  // ----------------

  const projectTypeOptions = useMemo(() => (
    [{ label: 'None', value: -1 }].concat(projectTypes.map(({ name, id }) => ({ label: name, value: id })))
  ), [projectTypes]);

  const labelOptions = useMemo(() => (
    labels
      .filter((label) => label.type === 'projects')
      .map(({ title, id }) => ({ label: title, value: id }))
  ), [labels]);

  const statusOptions = useMemo(() => [
    { label: 'Active', value: 1 },
    { label: 'Archived', value: 0 },
  ], []);

  const contractExpirationOptions = useMemo(() => [
    { label: 'Valid Contract', value: 'valid' },
    { label: 'Expiring Soon', value: 'soon' },
    { label: 'Expired', value: 'expired' },
  ], []);

  // Create bucket options for each bucket template
  const projectBucketTemplates = useMemo(() => getProjectBucketTemplates(bucketTemplates), [bucketTemplates]);
  const bucketTemplateOptions = useMemo(() => {
    const bucketsByTemplateId = buckets.reduce((acc, bucket) => {
      if (!acc[bucket.bucketTemplateId]) {
        acc[bucket.bucketTemplateId] = [];
      }
      acc[bucket.bucketTemplateId].push(bucket);
      return acc;
    }, {});

    return projectBucketTemplates.reduce((acc, template) => {
      const templateBuckets = bucketsByTemplateId[template.id] ?? [];

      acc[template.id] = templateBuckets.map(({ name, id }) => ({
        label: name,
        value: id,
      }));
      return acc;
    }, {});
  }, [projectBucketTemplates, buckets]);

  // Extract dropdown fields from custom field template (similar to boards)
  const relevantCustomFields = useMemo(() => {
    const relevantFields = [];
    customFieldTemplate?.sections?.forEach(({ name: sectionName, fields = [] } = {}) => {
      fields?.forEach((field = {}) => {
        const { id, selectedType, configProps = {} } = field;
        const {
          dataType,
          title: fieldName,
          customOptions: fieldOptions = configProps.options || [],
        } = configProps;
        if (selectedType === 'dropdown' && VALID_DROPDOWN_DATA_TYPES.has(dataType)) {
          relevantFields.push({
            id,
            name: `${sectionName} - ${fieldName}`,
            dataType,
            fieldOptions,
          });
        }
      });
    });
    return relevantFields;
  }, [customFieldTemplate]);

  // Build filter view fields including dynamic dropdown fields and bucket templates
  const allFilterViewFields = useMemo(() => {
    const fields = [...PROJECT_FILTER_VIEW_FIELDS];
    relevantCustomFields.forEach((field) => {
      fields.push({ field: field.id, defaultValue: [] });
    });
    // Add bucket template fields
    projectBucketTemplates.forEach((template) => {
      fields.push({ field: template.id, defaultValue: [] });
    });
    return fields;
  }, [relevantCustomFields, projectBucketTemplates]);

  // User options for dropdown selectors
  const userOptions = useMemo(() => (
    users
      .filter((user) => user.active)
      .map((user) => ({
        value: user.id,
        label: user.name,
      }))
  ), [users]);

  // Use defaults from projectFilters.js for the custom tab
  const defaultFormValues = useMemo(() => {
    const resetValues = {
      projectTypeId: defaultFilters.projectTypeId || [],
      labels: [],
      active: defaultFilters.active || [],
      contractExpiration: defaultFilters.contractExpiration || [],
    };
    projectBucketTemplates.forEach((template) => {
      resetValues[template.id] = [];
    });
    relevantCustomFields.forEach((field) => {
      resetValues[field.id] = [];
    });
    return resetValues;
  }, [relevantCustomFields, projectBucketTemplates]);

  const resetFields = () => {
    form.setFieldsValue(defaultFormValues);
  };

  const onSelectChange = useCallback((val, option) => {
    form.setFieldsValue({
      [option]: val,
    });
  }, [form]);

  const onApply = useCallback(async () => {
    const values = form.getFieldsValue();

    if (filterView?.id) {
      values.filterView = filterView.id;
    } else {
      values.filterView = null;
      values.filterViewName = null;
    }

    // Extract custom field filters (non-standard filter keys)
    const customFieldFilterIds = new Set(relevantCustomFields.map((f) => f.id));
    const customFilters = {};
    Object.entries(values).forEach(([key, value]) => {
      if (customFieldFilterIds.has(key) && Array.isArray(value) && value.length > 0) {
        customFilters[key] = value;
      }
    });

    // Fetch filtered project IDs if custom field filters exist
    if (Object.keys(customFilters).length > 0) {
      await dispatch(fetchFilteredProjectIds(customFilters));
    } else {
      // Clear filtered project IDs if no custom field filters
      await dispatch(fetchFilteredProjectIds(null));
    }

    await dispatch(updateProjectFilters(values));
    onClose();
  }, [filterView, relevantCustomFields, onClose, form]);

  const onSave = useCallback(async () => {
    const values = form.getFieldsValue();
    if (filterView?.id) {
      toggleSaving();
      const filters = allFilterViewFields.reduce((acc, { field, defaultValue }) => {
        const fullValue = values[field] ?? defaultValue;
        if (fullValue.length > 0) {
          acc.push({
            filterProperty: field,
            filterValue: JSON.stringify(fullValue),
            filterViewId: filterView.id,
          });
        }
        return acc;
      }, []);
      await dispatch(updateUserProjectFilterView(filterView.id, {
        name: values.filterViewName,
        typeId: currentProjectTypeId,
        filters,
      }));
      toggleSaving();
      onApply();
    } else {
      setIsModalOpen(true);
    }
  }, [
    filterView,
    activeFilters,
    currentProjectTypeId,
    allFilterViewFields,
    onApply,
  ]);

  const onDelete = useCallback(() => new Promise((resolve) => {
    CustomConfirmModal({
      title: 'Delete Filter View',
      content: (
        <p>
          Are you sure you wish to delete this filter view?
        </p>
      ),
      okText: 'Delete',
      cancelText: 'Cancel',
      async onOk() {
        resolve(await dispatch(
          deleteUserProjectFilterView(filterView.id),
        ));

        if (activeFilters.filterView === filterView.id) {
          dispatch(updateProjectFilters({
            filterView: null,
            filterViewName: null,
          }));
        } else {
          setActiveTab(activeFilters.filterView || 'custom');
        }

        setIsModalOpen(false);
      },
      onCancel() {
        resolve();
      },
    });
  }), [
    activeFilters,
    filterView,
    onClose,
  ]);

  const addNewFilterView = useCallback(async (filterViewName) => {
    const values = form.getFieldsValue();

    const {
      id,
      filterViewName: newFilterViewName,
    } = await dispatch(addUserProjectFilterView({
      name: filterViewName,
      typeId: currentProjectTypeId,
      filters: allFilterViewFields.map(({ field, defaultValue }) => ({
        filterProperty: field,
        filterValue: JSON.stringify(values[field] ?? defaultValue),
      })),
    }));

    onApply();
    setActiveTab(id);
    dispatch(updateProjectFilters({
      filterView: id,
      filterViewName: newFilterViewName,
    }));
  }, [
    form,
    onClose,
    setActiveTab,
    currentProjectTypeId,
    allFilterViewFields,
  ]);

  if (filterView?.id !== lastFilterId) {
    setLastFilterId(filterView?.id);
    // For custom tab: use activeFilters (last applied filters)
    // For saved filter view tabs: use the filter view's values
    const isCustomTab = !filterView?.id;
    const filtersToApply = isCustomTab ? activeFilters : currentFilters;

    const formValues = {
      ...defaultFormValues,
      filterViewName: getProperty(filtersToApply, 'filterViewName', ''),
      projectTypeId: getProperty(filtersToApply, 'projectTypeId', defaultFormValues.projectTypeId),
      labels: getProperty(filtersToApply, 'labels', defaultFormValues.labels),
      active: getProperty(filtersToApply, 'active', defaultFormValues.active),
      contractExpiration: getProperty(filtersToApply, 'contractExpiration', defaultFormValues.contractExpiration),
    };

    // Set dynamic dropdown field values
    relevantCustomFields.forEach((field) => {
      formValues[field.id] = getProperty(filtersToApply, field.id, []);
    });

    // Set bucket template field values
    projectBucketTemplates.forEach((template) => {
      formValues[template.id] = getProperty(filtersToApply, template.id, []);
    });

    form.setFieldsValue(formValues);
  }

  return (
    <>
      <div>
        <Row>
          <h2>{formTitle}</h2>
          <OnTraccrButton
            onClick={resetFields}
            style={{ marginLeft: 'auto' }}
            title="Reset"
          />
        </Row>
        <Form
          form={form}
          layout="vertical"
        >
          {
            filterView?.id
              && (
                <>
                  <Form.Item
                    name="filterViewName"
                    label="Filter View Name"
                    rules={[
                      { required: true, message: 'Filter View Name is required' },
                    ]}
                  >
                    <OnTraccrTextInput
                      style={{ width: '100%' }}
                      placeholder="Name"
                    />
                  </Form.Item>
                  <Divider />
                </>
              )
            }

          <Form.Item
            name="active"
            label="Status"
          >
            <Select
              mode="multiple"
              onChange={(val) => onSelectChange(val, 'active')}
              allowClear
              options={statusOptions}
              placeholder="Select status"
              optionFilterProp="label"
            />
          </Form.Item>
          {projectTypeOptions.length > 1 && !currentProjectTypeId && (
            <Form.Item
              name="projectTypeId"
              label={`${t('Project')} Type`}
            >
              <Select
                onChange={(val) => onSelectChange(val, 'projectTypeId')}
                allowClear
                options={projectTypeOptions}
                placeholder={`Select ${t('Project').toLowerCase()} type`}
                mode="multiple"
                optionFilterProp="label"
              />
            </Form.Item>
          )}
          {labelOptions.length > 0 && (
            <Form.Item
              name="labels"
              label="Labels"
            >
              <Select
                onChange={(val) => onSelectChange(val, 'labels')}
                allowClear
                options={labelOptions}
                placeholder="Select labels"
                mode="multiple"
                optionFilterProp="label"
              />
            </Form.Item>
          )}
          <Form.Item
            name="contractExpiration"
            label="Contract Status"
          >
            <Select
              mode="multiple"
              onChange={(val) => onSelectChange(val, 'contractExpiration')}
              allowClear
              options={contractExpirationOptions}
              placeholder="Select contract status"
              optionFilterProp="label"
            />
          </Form.Item>
          {projectBucketTemplates.length > 0 && (
            <>
              <Divider />
              <Row>
                <h3>Buckets</h3>
              </Row>
            </>
          )}
          {projectBucketTemplates.map(({ id, name }) => (
            <Form.Item
              key={id}
              name={id}
              label={name}
            >
              <Select
                mode="multiple"
                onChange={(val) => onSelectChange(val, id)}
                allowClear
                options={bucketTemplateOptions[id]}
                placeholder={`Select ${name}`}
                optionFilterProp="label"
              />
            </Form.Item>
          ))}
          {relevantCustomFields.length > 0 && (
            <>
              <Divider />
              <Row>
                <h3>Custom Fields</h3>
              </Row>
            </>
          )}
          {relevantCustomFields.map(({
            id, name, dataType, fieldOptions,
          }) => (
            <Form.Item
              key={id}
              name={id}
              label={name}
              style={{ marginBottom: 16 }}
            >
              <BoardDropdownFieldSelector
                dataType={dataType}
                divisionIds={Object.keys(divisions)}
                users={userOptions}
                options={fieldOptions}
              />
            </Form.Item>
          ))}
        </Form>
      </div>
      <div className="drawer-footer">
        <Row justify="end" gutter={10}>
          <div style={{ marginRight: 'auto' }}>
            <OnTraccrButton
              title="Cancel"
              type="cancel"
              onClick={onClose}
            />
            {
              filterView?.id
                && (
                  <OnTraccrButton
                    title="Delete"
                    type="back"
                    onClick={onDelete}
                    style={{ marginLeft: 8 }}
                  />
                )
            }
          </div>
          <OnTraccrButton
            title={`${!filterView?.id ? 'Create' : 'Save'} and Apply`}
            onClick={onSave}
            style={{ marginRight: 8 }}
            loading={isSaving}
          />
          <OnTraccrButton
            title="Apply"
            onClick={onApply}
          />
        </Row>
      </div>
      <SimpleTextInputModal
        title="Add Filter View"
        visible={isModalOpen}
        placeholder="Enter Filter View name"
        onClose={() => setIsModalOpen(false)}
        onSave={addNewFilterView}
      />
    </>
  );
}

ProjectsCustomFilterTab.propTypes = {
  projectTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  ),
  labels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.string,
    }),
  ),
  onClose: PropTypes.func.isRequired,
  filterView: PropTypes.shape({
    filterViewName: PropTypes.string,
    id: PropTypes.string,
    projectTypeId: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    labels: PropTypes.arrayOf(PropTypes.string),
    active: PropTypes.arrayOf(PropTypes.number),
    contractExpiration: PropTypes.arrayOf(PropTypes.string),
    isFavourite: PropTypes.bool,
  }),
  setActiveTab: PropTypes.func.isRequired,
  currentProjectTypeId: PropTypes.string,
};

ProjectsCustomFilterTab.defaultProps = {
  projectTypes: [],
  labels: [],
  filterView: null,
  currentProjectTypeId: null,
};
